import { Switch } from 'antd'
import { useEffect, useState } from 'react'
import { ImageAssetPickerField } from '../../../components/ImageAssetPicker'
import type { GraphicPageBlock } from '../../../graphicPageTypes'
import { DEFAULT_CROP_DESKTOP, DEFAULT_CROP_MOBILE } from './CropEditor'
import VisualCropEditor from './VisualCropEditor'
import RichTextEditor from './RichTextEditor'
import EntryPickerSelect from './EntryPickerSelect'
import { siteAssetVariantUrl } from '../../../api'
import './graphic-renderer.css'

const ASPECTS = [
  { value: 'original', label: '原始比例' },
  { value: '16:9', label: '16:9' },
  { value: '4:3', label: '4:3' },
  { value: '3:2', label: '3:2' },
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
]
const LAYOUTS = [
  { value: 'standard', label: '标准' },
  { value: 'wide', label: '宽幅' },
  { value: 'full', label: '通栏' },
]
const ALIGNS = [
  { value: 'left', label: '左对齐' },
  { value: 'center', label: '居中' },
  { value: 'right', label: '右对齐' },
]

/** One editing form per LiangCollection block type (audit doc §2's
 * `DEFAULT_CONTENT_JSON`/`sanitize_block_content_json` whitelist,
 * ported 1:1 into `graphic_page_service.py`) — this is the
 * MODULE_TYPES_IMPLEMENTED checklist made real: every type below has a
 * genuine, functional editing surface, not just a label. */
export default function BlockEditPanel({
  workspaceId,
  siteId,
  block,
  onContentChange,
  onAssetChange,
  onLinkedEntryChange,
}: {
  workspaceId: string
  siteId: string
  block: GraphicPageBlock
  onContentChange: (patch: Record<string, unknown>) => void
  onAssetChange: (assetId: string | null) => void
  onLinkedEntryChange: (entryId: string | null) => void
}) {
  const c = block.content || {}

  switch (block.block_type) {
    case 'heading':
      return (
        <>
          <div className="ge-field">
            <label className="ge-field-label">标题文字</label>
            <input type="text" value={String(c.text || '')} onChange={(e) => onContentChange({ text: e.target.value })} placeholder="输入标题" />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">级别</label>
            <select value={Number(c.level) || 2} onChange={(e) => onContentChange({ level: Number(e.target.value) })}>
              <option value={2}>H2（大）</option>
              <option value={3}>H3（中）</option>
              <option value={4}>H4（小）</option>
            </select>
          </div>
        </>
      )
    case 'text':
      return (
        <div className="ge-field">
          <label className="ge-field-label">正文</label>
          <RichTextEditor html={String(c.html || '')} onChange={(html) => onContentChange({ html })} />
        </div>
      )
    case 'quote':
      return (
        <>
          <div className="ge-field">
            <label className="ge-field-label">引用文字</label>
            <textarea rows={3} value={String(c.text || '')} onChange={(e) => onContentChange({ text: e.target.value })} />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">出处 / 作者</label>
            <input type="text" value={String(c.citation || '')} onChange={(e) => onContentChange({ citation: e.target.value })} />
          </div>
        </>
      )
    case 'divider':
      return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>分隔线无需配置。</p>
    case 'spacer':
      return (
        <div className="ge-field">
          <label className="ge-field-label">间隔高度</label>
          <select value={String(c.height || 'md')} onChange={(e) => onContentChange({ height: e.target.value })}>
            <option value="sm">小</option>
            <option value="md">中</option>
            <option value="lg">大</option>
          </select>
        </div>
      )
    case 'image':
      return (
        <>
          {!block.asset_id ? (
            <div className="ge-field">
              <label className="ge-field-label">图片</label>
              <ImageAssetPickerField workspaceId={workspaceId} siteId={siteId} assetId={block.asset_id} onChange={onAssetChange} purpose="graphic_replace" compact />
            </div>
          ) : null}
          <div className="ge-field-row">
            <div className="ge-field">
              <label className="ge-field-label">图片布局（宽度）</label>
              <select value={String(c.layout || 'standard')} onChange={(e) => onContentChange({ layout: e.target.value })}>
                {LAYOUTS.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
            <div className="ge-field">
              <label className="ge-field-label">图片比例</label>
              <select value={String(c.aspect || 'original')} onChange={(e) => onContentChange({ aspect: e.target.value })}>
                {ASPECTS.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
            <div className="ge-field">
              <label className="ge-field-label">文本对齐</label>
              <select value={String(c.align || 'center')} onChange={(e) => onContentChange({ align: e.target.value })}>
                {ALIGNS.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="ge-field-row">
            <div className="ge-field">
              <label className="ge-field-label">替代文字（Alt）</label>
              <input type="text" value={String(c.alt || '')} placeholder="用于可访问性，可选" onChange={(e) => onContentChange({ alt: e.target.value })} />
            </div>
            <div className="ge-field">
              <label className="ge-field-label">图片说明</label>
              <input type="text" value={String(c.caption || '')} placeholder="图注，可选" onChange={(e) => onContentChange({ caption: e.target.value })} />
            </div>
          </div>
          <div className="ge-field-row">
            <div className="ge-field">
              <label className="ge-field-label">来源</label>
              <input type="text" value={String(c.source || '')} onChange={(e) => onContentChange({ source: e.target.value })} />
            </div>
            <div className="ge-field">
              <label className="ge-field-label">版权信息</label>
              <input type="text" value={String(c.copyright || '')} onChange={(e) => onContentChange({ copyright: e.target.value })} />
            </div>
          </div>
          {block.asset_id ? (
            <div className="ge-field">
              <label className="ge-field-label">图片</label>
              <ImageAssetPickerField workspaceId={workspaceId} siteId={siteId} assetId={block.asset_id} onChange={onAssetChange} purpose="graphic_replace" compact label="更换图片" />
            </div>
          ) : null}
        </>
      )
    case 'collection_item':
      return (
        <CollectionItemBlockEditor
          workspaceId={workspaceId}
          siteId={siteId}
          block={block}
          onContentChange={onContentChange}
          onAssetChange={onAssetChange}
          onLinkedEntryChange={onLinkedEntryChange}
        />
      )
    case 'exhibition_ref':
    case 'research_ref':
      return (
        <div className="ge-field">
          <label className="ge-field-label">关联{block.block_type === 'exhibition_ref' ? '专题' : '文章'}</label>
          <EntryPickerSelect
            workspaceId={workspaceId}
            siteId={siteId}
            contentType={block.block_type === 'exhibition_ref' ? 'exhibition' : 'research'}
            value={block.linked_entry_id}
            onChange={(v) => onLinkedEntryChange((v as string) || null)}
          />
        </div>
      )
    case 'featured_collection':
    case 'featured_exhibition':
    case 'featured_research': {
      const contentType = block.block_type === 'featured_collection' ? 'item' : block.block_type === 'featured_exhibition' ? 'exhibition' : 'research'
      const kindLabel = block.block_type === 'featured_collection' ? '条目' : block.block_type === 'featured_exhibition' ? '专题' : '文章'
      return (
        <div className="ge-field">
          <label className="ge-field-label">精选{kindLabel}（最多 24 项）</label>
          <EntryPickerSelect
            workspaceId={workspaceId}
            siteId={siteId}
            contentType={contentType}
            multiple
            value={(c.item_ids as string[]) || []}
            onChange={(v) => onContentChange({ item_ids: (v as string[]) || [] })}
          />
        </div>
      )
    }
    case 'video':
      return (
        <>
          <div className="ge-field">
            <label className="ge-field-label">标题</label>
            <input type="text" value={String(c.title || '')} onChange={(e) => onContentChange({ title: e.target.value })} />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">视频链接（外链或 mp4 直链）</label>
            <input type="text" value={String(c.external_url || '')} onChange={(e) => onContentChange({ external_url: e.target.value })} placeholder="https://…" />
          </div>
          <div className="ge-field">
            {/* LiangCollection parity: video blocks support a poster image
             * shown before playback (`set-video-poster/` in the source —
             * see LIANGCOLLECTION_MEDIA_PICKER_REFERENCE.md §2). Reuses the
             * same shared MediaPicker/AssetPickerField as every other
             * image-bearing block — no second media system. */}
            <label className="ge-field-label">封面图（播放前展示，可选）</label>
            <ImageAssetPickerField workspaceId={workspaceId} siteId={siteId} assetId={block.asset_id} onChange={onAssetChange} purpose="graphic_replace" />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">图注</label>
            <input type="text" value={String(c.caption || '')} onChange={(e) => onContentChange({ caption: e.target.value })} />
          </div>
          <label className="ge-checkbox-label">
            <Switch size="small" checked={c.controls !== false} onChange={(v) => onContentChange({ controls: v })} /> 显示播放控制条
          </label>
        </>
      )
    case 'audio':
      return (
        <>
          <div className="ge-field">
            <label className="ge-field-label">标题</label>
            <input type="text" value={String(c.title || '')} onChange={(e) => onContentChange({ title: e.target.value })} />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">音频链接</label>
            <input type="text" value={String(c.external_url || '')} onChange={(e) => onContentChange({ external_url: e.target.value })} placeholder="https://…" />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">图注</label>
            <input type="text" value={String(c.caption || '')} onChange={(e) => onContentChange({ caption: e.target.value })} />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">文字稿</label>
            <textarea rows={3} value={String(c.transcript || '')} onChange={(e) => onContentChange({ transcript: e.target.value })} />
          </div>
        </>
      )
    case 'hero': {
      const imageUrl = block.asset_id ? siteAssetVariantUrl(workspaceId, block.asset_id, 'web') : null
      return (
        <>
          <div className="ge-hero-canvas-row">
            <HeroLivePreview imageUrl={imageUrl} content={c} device="desktop" />
            <HeroLivePreview imageUrl={imageUrl} content={c} device="mobile" />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">Hero 图片</label>
            <ImageAssetPickerField workspaceId={workspaceId} siteId={siteId} assetId={block.asset_id} onChange={onAssetChange} purpose="graphic_replace" aspectRatio="16 / 9" />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">标题</label>
            <input type="text" value={String(c.title || '')} onChange={(e) => onContentChange({ title: e.target.value })} />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">副标题</label>
            <input type="text" value={String(c.subtitle || '')} onChange={(e) => onContentChange({ subtitle: e.target.value })} />
          </div>
          <div className="ge-field">
            <label className="ge-field-label">文字位置</label>
            <div className="ge-hero-pos-grid">
              {[
                ['left-top', '左上'], ['left-center', '左中'], ['left-bottom', '左下'],
                ['center', '居中'],
                ['right-top', '右上'], ['right-center', '右中'], ['right-bottom', '右下'],
              ].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  className={`ge-btn ge-btn-sm ${((c.text_position as string) || 'left-bottom') === val ? 'is-active' : ''}`}
                  onClick={() => onContentChange({ text_position: val })}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="ge-field-row">
            <div className="ge-field">
              <label className="ge-field-label">文字对齐</label>
              <select value={String(c.text_align || 'left')} onChange={(e) => onContentChange({ text_align: e.target.value })}>
                <option value="left">左对齐</option>
                <option value="center">居中</option>
                <option value="right">右对齐</option>
              </select>
            </div>
            <div className="ge-field">
              <label className="ge-field-label">标题字体</label>
              <select value={String(c.font_family || 'system-sans')} onChange={(e) => onContentChange({ font_family: e.target.value })}>
                <option value="system-sans">系统无衬线</option>
                <option value="source-han-sans">思源黑体</option>
                <option value="source-han-serif">思源宋体</option>
                <option value="lxgw-wenkai">霞鹜文楷</option>
              </select>
            </div>
            <div className="ge-field">
              <label className="ge-field-label">文字颜色</label>
              <select value={String(c.text_color || 'white')} onChange={(e) => onContentChange({ text_color: e.target.value })}>
                <option value="white">白色</option>
                <option value="black">黑色</option>
                <option value="auto">自动</option>
              </select>
            </div>
          </div>
          <div className="ge-field-row">
            <div className="ge-field">
              <label className="ge-field-label">遮罩强度</label>
              <select value={String(c.overlay_strength || 'medium')} onChange={(e) => onContentChange({ overlay_strength: e.target.value })}>
                <option value="none">无</option>
                <option value="light">浅</option>
                <option value="medium">中</option>
                <option value="deep">深</option>
              </select>
            </div>
            <div className="ge-field">
              <label className="ge-field-label">高度</label>
              <select value={String(c.height_mode || 'fullscreen')} onChange={(e) => onContentChange({ height_mode: e.target.value })}>
                <option value="fullscreen">全屏</option>
                <option value="large">大</option>
                <option value="standard">标准</option>
              </select>
            </div>
            <div className="ge-field">
              <label className="ge-field-label">标题字号</label>
              <select value={String(c.title_size || 'medium')} onChange={(e) => onContentChange({ title_size: e.target.value })}>
                <option value="small">小</option>
                <option value="medium">中</option>
                <option value="large">大</option>
                <option value="xlarge">特大</option>
              </select>
            </div>
          </div>
          <VisualCropEditor
            label="桌面端构图（16:9）"
            imageUrl={imageUrl}
            value={(c.crop_desktop as never) || null}
            defaultValue={DEFAULT_CROP_DESKTOP}
            aspectRatio={16 / 9}
            onChange={(v) => onContentChange({ crop_desktop: v })}
          />
          <VisualCropEditor
            label="移动端构图（3:4）"
            imageUrl={imageUrl}
            value={(c.crop_mobile as never) || null}
            defaultValue={DEFAULT_CROP_MOBILE}
            aspectRatio={3 / 4}
            onChange={(v) => onContentChange({ crop_mobile: v })}
          />
        </>
      )
    }
    default:
      return <p>暂不支持编辑此类型：{block.block_type}</p>
  }
}

const HERO_FONT_STACKS: Record<string, string | undefined> = {
  'system-sans': undefined,
  'source-han-sans': '"PingFang SC", "Noto Sans CJK SC", "Microsoft YaHei", sans-serif',
  'source-han-serif': '"Songti SC", "Noto Serif CJK SC", "SimSun", serif',
  'lxgw-wenkai': '"LXGW WenKai", "Kaiti SC", "STKaiti", cursive, serif',
}

function CollectionItemBlockEditor({
  workspaceId,
  siteId,
  block,
  onContentChange,
  onAssetChange,
  onLinkedEntryChange,
}: {
  workspaceId: string
  siteId: string
  block: GraphicPageBlock
  onContentChange: (patch: Record<string, unknown>) => void
  onAssetChange: (assetId: string | null) => void
  onLinkedEntryChange: (entryId: string | null) => void
}) {
  const c = block.content || {}
  const linked = block.linked_entry
  const [customImageMode, setCustomImageMode] = useState(!!block.asset_id)

  useEffect(() => {
    setCustomImageMode(!!block.asset_id)
  }, [block.id, block.asset_id])

  return (
    <>
      <div className="ge-field">
        <label className="ge-field-label">关联条目</label>
        {linked ? (
          <div className="ge-entry-link-row">
            {linked.cover_asset_id ? (
              <img className="ge-entry-link-thumb" src={siteAssetVariantUrl(workspaceId, linked.cover_asset_id, 'thumbnail')} alt="" />
            ) : null}
            <div className="ge-entry-link-text">
              <strong>{linked.label}</strong>
            </div>
          </div>
        ) : null}
        <EntryPickerSelect
          workspaceId={workspaceId}
          siteId={siteId}
          contentType="item"
          value={block.linked_entry_id}
          onChange={(v) => onLinkedEntryChange((v as string) || null)}
          placeholder={linked ? '更换条目…' : '搜索并选择条目…'}
        />
      </div>
      <div className="ge-field">
        <label className="ge-field-label">图片</label>
        <div className="ge-image-source-row">
          <label className="ge-radio-inline">
            <input
              type="radio"
              name={`item-img-${block.id}`}
              checked={!customImageMode}
              onChange={() => {
                setCustomImageMode(false)
                onAssetChange(null)
              }}
            />
            使用条目封面
          </label>
          <label className="ge-radio-inline">
            <input type="radio" name={`item-img-${block.id}`} checked={customImageMode} onChange={() => setCustomImageMode(true)} />
            自定义图片
          </label>
        </div>
        {customImageMode ? (
          <>
            <ImageAssetPickerField
              workspaceId={workspaceId}
              siteId={siteId}
              assetId={block.asset_id}
              onChange={(assetId) => {
                onAssetChange(assetId)
                if (!assetId) setCustomImageMode(false)
              }}
              purpose="graphic_replace"
              compact
              aspectRatio="4 / 3"
              label={block.asset_id ? '更换自定义图片' : '选择自定义图片'}
            />
            {block.asset_id ? (
              <button type="button" className="ge-btn ge-btn-sm" style={{ marginTop: 8 }} onClick={() => onAssetChange(null)}>
                恢复使用条目封面
              </button>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="ge-field-row">
        <label className="ge-checkbox-label">
          <input type="checkbox" checked={c.show_title !== false} onChange={(e) => onContentChange({ show_title: e.target.checked })} />
          显示标题
        </label>
        <label className="ge-checkbox-label">
          <input type="checkbox" checked={c.show_creator !== false} onChange={(e) => onContentChange({ show_creator: e.target.checked })} />
          显示作者
        </label>
        <label className="ge-checkbox-label">
          <input type="checkbox" checked={c.show_period !== false} onChange={(e) => onContentChange({ show_period: e.target.checked })} />
          显示年代
        </label>
      </div>
      <label className="ge-checkbox-label" style={{ marginBottom: 12 }}>
        <input type="checkbox" checked={!!c.show_accession_number} onChange={(e) => onContentChange({ show_accession_number: e.target.checked })} />
        显示编号
      </label>
      <div className="ge-field">
        <label className="ge-field-label">自定义图注（覆盖默认标题）</label>
        <input type="text" value={String(c.custom_caption || '')} onChange={(e) => onContentChange({ custom_caption: e.target.value })} />
      </div>
    </>
  )
}

/**
 * Hero block live composition preview — 1:1 port of LiangCollection's
 * `heroPreviewCanvasHtml`/`syncHeroLivePreview` (`content_editor.js:778-825`):
 * a mini desktop/mobile "canvas" inside the block editor itself that
 * updates on every keystroke. Deliberately reuses the *exact same*
 * `gr-hero-*` classes `GraphicTreeRenderer.tsx`/`graphic-renderer.css`
 * apply to the real published/preview render (not a second hand-maintained
 * style set), scaled down via `ge-hero-canvas-mini`, so what you see here
 * is guaranteed pixel-identical (modulo scale) to the real page.
 */
function HeroLivePreview({
  imageUrl,
  content,
  device,
}: {
  imageUrl: string | null
  content: Record<string, unknown>
  device: 'desktop' | 'mobile'
}) {
  const pos = String(content.text_position || 'left-bottom')
  const color = String(content.text_color || 'white')
  const overlay = String(content.overlay_strength || 'medium')
  const heightMode = content.height_mode === 'large' ? 'is-large' : content.height_mode === 'standard' ? 'is-standard' : 'is-fullscreen'
  const size = String(content.title_size || 'medium')
  const title = String(content.title || '')
  const subtitle = String(content.subtitle || '')
  const textAlign = content.text_align as string | undefined
  const fontFamily = HERO_FONT_STACKS[String(content.font_family || 'system-sans')]
  return (
    <div className="ge-hero-canvas-mini">
      <span className="ge-hero-canvas-badge">{device === 'mobile' ? '手机构图预览' : '桌面构图预览'}</span>
      <div className={`gr-hero-block ${heightMode} pos-${pos} overlay-${overlay} color-${color} ge-hero-canvas-frame-${device}`}>
        {imageUrl ? <img className="gr-hero-img" src={imageUrl} alt="" /> : <div className="gr-hero-img gr-image-placeholder" />}
        <div className="gr-hero-overlay" />
        <div className={`gr-hero-content title-${size}`} style={{ textAlign: textAlign as never, fontFamily }}>
          {title ? <h2>{title}</h2> : <h2 className="is-placeholder">输入标题后即时预览</h2>}
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      </div>
    </div>
  )
}
